import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiEdit } from "react-icons/fi";
import { IoEyeOutline } from "react-icons/io5";
import MasterShell from "../common/MasterShell";
import MasterListPage from "../common/MasterListPage";
import MasterToast from "../common/MasterToast";
import StatusBadge from "../common/StatusBadge";
import useRouteToast from "../common/useRouteToast";
import { getStations } from "../../services/stationService";
import {
  extractApiErrorMessage,
  formatMasterDate,
  matchesSearch,
} from "../../utils/masterHelpers";
import { useCapabilities } from "../../contexts/CapabilitiesContext";
import { getMasterHierarchyAccess } from "../../utils/hierarchyScope";
import "./Stations.css";

export default function StationList() {
  const navigate = useNavigate();
  const { clearToast, toast } = useRouteToast();
  const { canAny, loading: capabilitiesLoading, user } = useCapabilities();
  const canCreateStation = canAny("stations", ["create", "manage"]);
  const canReadStation = canAny("stations", ["read", "manage"]);
  const canUpdateStation = canAny("stations", ["update", "manage"]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [stations, setStations] = useState([]);

  const scopeAccess = useMemo(
    () => getMasterHierarchyAccess({ user }),
    [user]
  );
  const { fixedDistrictId, fixedStationId, fixedSubDivisionId } = scopeAccess;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const stationParams = fixedStationId
        ? { station_id: fixedStationId }
        : fixedSubDivisionId
        ? {
            sub_division_id: fixedSubDivisionId,
            ...(fixedDistrictId ? { district_id: fixedDistrictId } : {}),
          }
        : fixedDistrictId
        ? { district_id: fixedDistrictId }
        : {};
      const stationResponse = await getStations(stationParams);

      setStations(Array.isArray(stationResponse) ? stationResponse : []);
    } catch (apiError) {
      setError(extractApiErrorMessage(apiError, "Failed to load station data."));
      setStations([]);
    } finally {
      setLoading(false);
    }
  }, [fixedDistrictId, fixedStationId, fixedSubDivisionId]);

  useEffect(() => {
    if (capabilitiesLoading) {
      return;
    }

    fetchData();
  }, [capabilitiesLoading, fetchData]);

  const rows = useMemo(
    () =>
      stations.map((item) => ({
        ...item,
        district_name:
          item.district_name ||
          item.district ||
          (item.district_id === user?.district_id ? user?.district_name : "-") ||
          "-",
        sub_division_name:
          item.sub_division_name ||
          item.sub_division ||
          (item.sub_division_id === user?.sub_division_id
            ? user?.sub_division_name
            : "-") ||
          "-",
      })),
    [stations, user]
  );

  const columns = [
    {
      key: "rowNumber",
      label: "Sr. No.",
      render: (_, __, rowNumber) => rowNumber,
    },
    { key: "code", label: "Code" },
    { key: "name", label: "Station Name" },
    { key: "district_name", label: "District" },
    { key: "sub_division_name", label: "Sub-division" },
    { key: "state", label: "State" },
    {
      key: "status",
      label: "Status",
      render: (row) => <StatusBadge value={row.is_active} />,
    },
    {
      key: "created_at",
      label: "Created At",
      render: (row) => formatMasterDate(row.created_at),
    },
    {
      key: "actions",
      label: "Actions",
      render: (row) => (
        <div className="masters-table__actions">
          {canReadStation ? (
            <button
              className="masters-icon-btn masters-icon-btn--view"
              onClick={() => navigate(`/stations/${row.id}/view`)}
              type="button"
            >
              <IoEyeOutline />
            </button>
          ) : null}
          {canUpdateStation && (
            <button
              className="masters-icon-btn masters-icon-btn--edit"
              onClick={() => navigate(`/stations/${row.id}/edit`)}
              type="button"
            >
              <FiEdit />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <MasterShell pageClassName="stations-module">
      <MasterListPage
        addLabel={canCreateStation ? "Add Station" : undefined}
        addPath={canCreateStation ? "/stations/add" : undefined}
        className="new_master-records-list"
        columns={columns}
        emptyMessage="Station records will appear here after they are created."
        error={error}
        isLoading={loading || capabilitiesLoading}
        onRetry={fetchData}
        rows={rows}
        searchPlaceholder="Search by code, station, district, sub-division, or state"
        searchPredicate={(row, searchTerm) =>
          matchesSearch(
            [
              row.code,
              row.name,
              row.district_name,
              row.sub_division_name,
              row.state,
            ],
            searchTerm
          )
        }
        title="Stations"
      />
      <MasterToast
        message={toast?.message || ""}
        onClose={clearToast}
        open={Boolean(toast)}
        severity={toast?.severity || "success"}
      />
    </MasterShell>
  );
}
